<script lang="ts" setup>

import { computed, onMounted, watch } from "vue";
import { addLayer, injectMap } from "../../../utils/MapUtils";
import { AddLayerObject, ExpressionSpecification, GeoJSONSource } from "maplibre-gl";
import circle from "@turf/circle";
import destination from "@turf/destination";
import envelope from "@turf/envelope";
import transformRotate from "@turf/transform-rotate";
import { useStore } from "@/store/Store";
import { storeToRefs } from "pinia";
import { TFeature, TFeatureCollection } from '@/shared/types/geojson';
import { BASE_IMAGE_WIDTH } from "@/shared/renderers/base/RenderUtils";
import { buildIconSizeExpression } from "@/shared/utils/iconSize";
import { imageUrl } from "@/utils/ImageUrl";

// Track latest requested URL per image id
const loadedImageUrls = new Map<string, string>();

const $map = injectMap();

const store = useStore();
const { computedShapes } = storeToRefs(store);


const shapesCollection = computed(() => {
	return {
		type: "FeatureCollection",
		features: computedShapes.value,
	} as const
});

const imageShapesCollection = computed<TFeatureCollection>(() => {
	let shapes = computedShapes.value
		.filter(feature => feature.geometry.type === "Point" && feature.properties.shapeType === "image")

	return {
		type: "FeatureCollection",
		features: shapes as TFeature[],
	}
});

/**
 * Build icon-size expression using the store's usedZoomStops.
 * Reads the pre-computed per-feature __sz_N properties.
 */
const nonWorldSizeExpression = computed<ExpressionSpecification>(() => {
	return buildIconSizeExpression(store.usedZoomStops);
});

function getSource(name: string): GeoJSONSource {
	return $map.getSource(name) as GeoJSONSource;
}

// Load or update an image in the map when URL (or requested size) changes
async function loadImageToMap(url: string, imageId: string): Promise<void> {
	if (loadedImageUrls.get(imageId) === url && $map.hasImage(imageId)) {
		return;
	}

	loadedImageUrls.set(imageId, url);

	try {
		const image = await $map.loadImage(url);

		if (loadedImageUrls.get(imageId) !== url) {
			return;
		}

		if ($map.hasImage(imageId)) {
			$map.removeImage(imageId);
		}

		if (!$map.hasImage(imageId)) {
			$map.addImage(imageId, image.data);
		}
	} catch (error) {
		console.error(`Failed to load image: ${url}`, error);
	}
}

// Load all images from the image shapes collection
function loadAllImages(): void {
	for (const feature of imageShapesCollection.value.features) {
		const url = feature.properties?.imageUrl;
		const imageId = feature.properties?.__imageId;

		if (url && imageId) {
			const loadedWidth = feature.properties?.__loadedWidth;
			loadImageToMap(imageUrl(url, loadedWidth), imageId);
		}
	}
}

function initMap() {
	addLayer($map, {
		id: "map-polygons",
		type: 'fill',
		source: {
			type: 'geojson',
			data: shapesCollection.value
		},
		layout: {
		},
		paint: {
			// "fill-antialias": true,
			"fill-color": ["to-color", ["get", "color"], "#000"],
			"fill-outline-color": "transparent"
			// "fill-opacity": ["/", ["to-number", ["get", "opacity"]], 100],
			// "fill-outline-color": ["to-color", ["get", "outlineColor"], "#000"],
		},
		filter: [
			"all",
			["==", ["geometry-type"], "Polygon"],
			['>=', ['zoom'], ["number", ["get", "zoom"], 0]],
			["==", ["get", "__excluded"], false]
		]

	}, false);

	addLayer($map, {
		id: "map-lines",
		type: 'line',
		source: {
			type: 'geojson',
			data: shapesCollection.value
		},
		layout: {
			'line-join': 'round',
			'line-cap': 'round'
		},
		paint: {
			"line-width":
				['interpolate',
					['linear'],
					['zoom'],
					11, ["case", ["boolean", ["get", "outlineScale"], false], 0.25, ["number", ["get", "outlineWidth"], 0.5]],
					22, ["number", ["get", "outlineWidth"], 0.5],
				],
			"line-color": ["to-color", ["get", "outlineColor"], "#000"],
		},
		filter: ['all',
			['>=', ['zoom'], ["number", ["get", "zoom"], 0]],
			["==", ["get", "__excluded"], false]
		]

	}, false);

	// Image shapes layer
	// IMPORTANT: Style is (kind of) duplicated in DrawStyle.ts - keep in sync
	const imageStyle = (worldSize: boolean): AddLayerObject => ({
		id: worldSize ? "map-image-shapes-world" : "map-image-shapes",
		type: 'symbol',
		source: {
			type: 'geojson',
			data: imageShapesCollection.value
		},
		layout: {
			"icon-image": ["get", "__imageId"],
			'icon-size': worldSize
				? [
					'interpolate',
					['exponential', 2], // Base 2 because world scale doubles every zoom level
					['zoom'],
					1, 0,
					22, ["/",
						["*", ["number", ["get", "width"], 1], BASE_IMAGE_WIDTH, 20],
						["get", "__loadedWidth"]
					]
				]
				: nonWorldSizeExpression.value,
			"icon-anchor": worldSize ? "center" : ["string", ["get", "anchor"], "center"],
			"icon-rotate": ["number", ["get", "rotation"], 0],
			"icon-allow-overlap": true,
			"icon-ignore-placement": true,
		},
		paint: {
			"icon-opacity": ["/", ["number", ["get", "opacity"], 100], 100],
		},
		filter: ["all",
			worldSize ? ["==", ["get", "worldSize"], true] : ["==", ["get", "worldSize"], false],
			['>=', ['zoom'], ["number", ["get", "zoom"], 0]],
			["==", ["get", "__excluded"], false],
			["has", "__imageId"]
		]
	});
	addLayer($map, imageStyle(false), false);
	addLayer($map, imageStyle(true), false);

	// Load initial images
	loadAllImages();
}



onMounted(() => {
	initMap();
});

watch(shapesCollection, (newValue) => {
	getSource("map-polygons").setData(newValue);
	getSource("map-lines").setData(newValue);
}, { deep: true });

watch(imageShapesCollection, (newValue) => {
	// Load any new images in the background
	loadAllImages();
	// Update the source data (features already have __sz_N from the store)
	getSource("map-image-shapes").setData(newValue);
	getSource("map-image-shapes-world").setData(newValue);
}, { deep: true });

// Update icon-size expression when keyframe configuration changes
watch(nonWorldSizeExpression, (expr) => {
	$map.setLayoutProperty("map-image-shapes", "icon-size", expr);
});

</script>

<template></template>

<style scoped></style>